import { Box, FormControlLabel, Stack, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

import { JobOpeningCrucialRequirementCategory } from '@types';
import { RadioButton, SelectableWrapper } from 'src/components';

interface IRequirementItemProps {
  category: JobOpeningCrucialRequirementCategory;
  label: string;
  name: string;
  description?: string | null;
  selectedValue: boolean | null | undefined;
  handleChange: (
    category: JobOpeningCrucialRequirementCategory,
    name: string,
    value: boolean,
  ) => void;
}

// Shared styles to avoid recreation on each render
const responsiveBoxStyles = (theme: any) => ({
  margin: '0 1rem 0.25rem 0',
  width: '400px',
  [theme.breakpoints.down('lg')]: {
    margin: '0 0 0.5rem 0',
    width: '100%',
  },
});

const selectableWrapperStyles = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'start',
  paddingRight: '0',
} as const;

const RadioOption: React.FC<{
  value: string;
  label: string;
  isSelected: boolean;
  onClick: (value: string) => void;
}> = ({ value, label, isSelected, onClick }) => (
  <Box
    display="flex"
    flexDirection="column"
    flexWrap="wrap"
    sx={responsiveBoxStyles}
  >
    <SelectableWrapper
      value={value}
      selectedValue={isSelected}
      onClick={onClick}
      sx={selectableWrapperStyles}
    >
      <FormControlLabel
        checked={isSelected}
        value={value}
        control={<RadioButton tabIndex={-1} />}
        label={
          <Typography variant="body1" sx={{ fontWeight: '500' }}>
            {label}
          </Typography>
        }
      />
    </SelectableWrapper>
  </Box>
);

export const RequirementItem: React.FC<IRequirementItemProps> = ({
  category,
  label,
  name,
  description,
  selectedValue,
  handleChange,
}) => {
  const onClick = (newValue: string) =>
    handleChange(category, name, newValue === 'true');

  const isSelected = Boolean(selectedValue);

  // Render simple chip-style requirement without description
  if (!description) {
    return (
      <Box
        sx={(theme) => ({
          cursor: 'pointer',
          background: isSelected
            ? theme.palette.highlight.accent
            : theme.palette.highlight.actionable,
          padding: '16px 24px',
        })}
        display="flex"
        gap={1}
        onClick={() => onClick(isSelected ? 'false' : 'true')}
      >
        <Typography>{label}</Typography>
        {isSelected && (
          <CheckIcon
            sx={(theme) => ({ color: theme.palette.text.secondary })}
          />
        )}
      </Box>
    );
  }

  // Render detailed requirement with Yes/No options
  return (
    <Stack gap={2}>
      <Stack gap={2}>
        <Typography variant="h2" marginBottom={0}>
          {label}
        </Typography>
        <Typography variant="body1">{description}</Typography>
      </Stack>
      <Stack direction="column" gap={1}>
        <RadioOption
          value="true"
          label="Yes"
          isSelected={selectedValue === true}
          onClick={onClick}
        />
        <RadioOption
          value="false"
          label="No"
          isSelected={selectedValue === false}
          onClick={onClick}
        />
      </Stack>
    </Stack>
  );
};
